/**
 * Экономика акции — тариф «Про».
 *
 * Слева партнёр вводит смету, справа видит, что из неё получилось. Все суммы
 * ходят целыми, в минорных единицах валюты: ввод разбирается в дираму один
 * раз, дальше ни одного деления на 100 до самого показа.
 *
 * Считает метрики сервер, а не этот экран. Иначе ROI в кабинете и ROI в
 * выгрузке однажды разойдутся на округлении, и доверять будет нечему.
 */
import type { Economics, EconomicsView } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import {
  Button, Card, Empty, Field, Icon, KeyRow, Skeleton,
  money, num, parseMoney, percentBp, timesBp,
} from '@allswipes/ui';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { canPublish, useConsole } from '../session';
import { useToast } from '../toast';

const EMPTY: Economics = {
  currency: 'TJS',
  budget: 0,
  prize_fund: 0,
  production: 0,
  promotion: 0,
  platform: 0,
  other: 0,
  prize_costs: {},
  avg_check: 0,
  margin_bp: 0,
  repeat_purchases: 0,
  horizon_days: 0,
};

export function EconomicsScreen() {
  const { id = '' } = useParams();
  const { me } = useConsole();
  const toast = useToast();
  const view = useResource(() => api.economics(id), [id]);

  const [form, setForm] = useState<Economics>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (view.data) {
      setForm({ ...EMPTY, ...view.data.report.input });
      setDirty(false);
    }
  }, [view.data]);

  // Тариф известен из сессии, поэтому заглушку показываем не дожидаясь отказа
  // сервера. Запрет всё равно на сервере — здесь только объяснение.
  if (me && me.brand.plan !== 'pro') {
    return (
      <Layout title="Экономика акции" subtitle="тариф «Про»">
        <Empty icon="chart" title="Экономика — в тарифе «Про»">
          Бюджет, расходы, возврат инвестиций, стоимость привлечения и разрезы аудитории. Провести
          акцию и выдать призы можно на любом тарифе — «Про» добавляет аналитику по ней.
        </Empty>
        <Card style={{ marginTop: 14, maxWidth: 560 }}>
          <div className="lab" style={{ marginBottom: 10 }}>
            Что открывается
          </div>
          <KeyRow label="Смета">бюджет, призовой фонд, производство, продвижение</KeyRow>
          <KeyRow label="Отдача">ROI, ROMI, прибыль с учётом маржи</KeyRow>
          <KeyRow label="Привлечение">CAC, LTV, отношение LTV к CAC, окупаемость</KeyRow>
          <KeyRow label="Аудитория">новые и вернувшиеся, регионы, частота участий</KeyRow>
        </Card>
      </Layout>
    );
  }

  if (view.loading) {
    return (
      <Layout title="Экономика акции">
        <Skeleton count={4} height={90} />
      </Layout>
    );
  }
  if (view.error || !view.data) {
    return (
      <Layout title="Экономика акции">
        <Empty icon="search" title="Не открылось">
          {view.error ?? 'Акция не найдена.'}
        </Empty>
      </Layout>
    );
  }

  const data: EconomicsView = view.data;
  const r = data.report;
  const editable = canPublish(me?.member.role);

  const patch = (changes: Partial<Economics>) => {
    setForm((f) => ({ ...f, ...changes }));
    setDirty(true);
  };
  const setPrizeCost = (index: number, minor: number) => {
    patch({ prize_costs: { ...(form.prize_costs ?? {}), [String(index)]: minor } });
  };

  const save = async () => {
    setSaving(true);
    setErrors({});
    try {
      await api.saveEconomics(id, form);
      toast('Смета сохранена');
      setDirty(false);
      view.reload();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields ?? {});
        toast(err.message);
      } else {
        toast('Не получилось сохранить');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout
      title={`Экономика · ${data.campaign.name}`}
      subtitle="смета и отдача акции — видно только в кабинете"
      action={
        <Link to={`/campaigns/${id}`} className="btn out">
          К акции
        </Link>
      }
    >
      <div className="stats" style={{ marginBottom: 18 }}>
        <Stat label="расходы по факту" value={money(r.spend_actual)} />
        <Stat
          label="бюджет"
          value={r.input.budget > 0 ? `${money(r.input.budget)} · ${percentBp(r.budget_used_bp)}%` : '—'}
        />
        <Stat
          label="прибыль с учётом маржи"
          value={r.ready ? money(r.net_profit) : '—'}
          tone={r.ready && r.net_profit < 0 ? 'bad' : undefined}
        />
        <Stat
          label="ROI"
          value={r.ready ? `${percentBp(r.roi_bp)}%` : '—'}
          tone={r.ready ? (r.roi_bp >= 0 ? 'good' : 'bad') : undefined}
        />
      </div>

      {r.missing && r.missing.length > 0 && (
        <Card style={{ marginBottom: 18 }}>
          <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
            <Icon name="alert" size={16} />
            <div>
              <div className="h2">Чтобы посчитать отдачу, не хватает</div>
              <ul className="sub" style={{ margin: '6px 0 0 18px' }}>
                {r.missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {r.notes && r.notes.length > 0 && (
        <Card style={{ marginBottom: 18 }}>
          <div className="lab" style={{ marginBottom: 8 }}>
            Что видно в цифрах
          </div>
          <div className="stack">
            {r.notes.map((note) => (
              <div key={note} className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
                <Icon name="eye" size={15} />
                <span className="sub">{note}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid c2" style={{ alignItems: 'start', gap: 18 }}>
        {/* ── смета ── */}
        <div className="stack">
          <Card>
            <div className="h2" style={{ marginBottom: 12 }}>
              Бюджет и расходы
            </div>
            <MoneyField
              label="Бюджет акции"
              hint="план, с которым сравниваются расходы"
              value={form.budget}
              error={errors['budget']}
              disabled={!editable}
              onChange={(v) => patch({ budget: v })}
            />
            <MoneyField
              label="Призовой фонд"
              hint="для розыгрыша — вся сумма фонда"
              value={form.prize_fund}
              error={errors['prize_fund']}
              disabled={!editable}
              onChange={(v) => patch({ prize_fund: v })}
            />
            <MoneyField
              label="Производство"
              hint="печать кодов, этикетки, QR"
              value={form.production}
              error={errors['production']}
              disabled={!editable}
              onChange={(v) => patch({ production: v })}
            />
            <MoneyField
              label="Продвижение"
              value={form.promotion}
              error={errors['promotion']}
              disabled={!editable}
              onChange={(v) => patch({ promotion: v })}
            />
            <MoneyField
              label="Платформа"
              value={form.platform}
              error={errors['platform']}
              disabled={!editable}
              onChange={(v) => patch({ platform: v })}
            />
            <MoneyField
              label="Прочее"
              value={form.other}
              error={errors['other']}
              disabled={!editable}
              onChange={(v) => patch({ other: v })}
            />
          </Card>

          <Card>
            <div className="h2" style={{ marginBottom: 6 }}>
              Допущения
            </div>
            <p className="sub" style={{ marginBottom: 12 }}>
              Из них считаются выручка, прибыль и LTV. Это оценки бренда, а не данные платформы, —
              в отчёте они помечены как ввод.
            </p>
            <MoneyField
              label="Средний чек участника"
              hint="каждое участие — подтверждённая покупка"
              value={form.avg_check}
              error={errors['avg_check']}
              disabled={!editable}
              onChange={(v) => patch({ avg_check: v })}
            />
            <Field label="Маржа, %" error={errors['margin_bp']}>
              <input
                className="input mono"
                type="number"
                min={0}
                max={100}
                step="0.1"
                disabled={!editable}
                value={form.margin_bp / 100}
                onChange={(e) => patch({ margin_bp: Math.round(Number(e.target.value) * 100) })}
              />
            </Field>
            <Field
              label="Покупок на клиента за горизонт"
              hint="сколько раз клиент вернётся — для LTV"
              error={errors['repeat_purchases']}
            >
              <input
                className="input mono"
                type="number"
                min={0}
                disabled={!editable}
                value={form.repeat_purchases}
                onChange={(e) => patch({ repeat_purchases: Number(e.target.value) })}
              />
            </Field>
            <Field label="Горизонт, дней" error={errors['horizon_days']}>
              <input
                className="input mono"
                type="number"
                min={0}
                disabled={!editable}
                value={form.horizon_days}
                onChange={(e) => patch({ horizon_days: Number(e.target.value) })}
              />
            </Field>
          </Card>

          {data.prizes.length > 0 && (
            <Card>
              <div className="h2" style={{ marginBottom: 6 }}>
                Себестоимость призов
              </div>
              <p className="sub" style={{ marginBottom: 12 }}>
                Цена за единицу. Расходы считаются по фактически выданным призам, а не по плану, —
                поэтому смета сходится с тем, что действительно отдано.
              </p>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Приз</th>
                    <th>Выдано</th>
                    <th>За единицу</th>
                    <th>Итого</th>
                  </tr>
                </thead>
                <tbody>
                  {data.prizes.map((p) => (
                    <tr key={p.index}>
                      <td>{p.label}</td>
                      <td className="mono">{num(p.issued)}</td>
                      <td>
                        <input
                          className="input mono"
                          style={{ width: 110 }}
                          disabled={!editable}
                          value={moneyInput(form.prize_costs?.[String(p.index)] ?? 0)}
                          onChange={(e) => setPrizeCost(p.index, parseMoney(e.target.value))}
                        />
                      </td>
                      <td className="mono">{money(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {editable && (
            <div className="row" style={{ gap: 8 }}>
              <Button variant="pri" disabled={saving || !dirty} onClick={save}>
                {saving ? 'Сохраняем…' : 'Сохранить смету'}
              </Button>
              {dirty && <span className="sub">Есть несохранённые изменения</span>}
            </div>
          )}
          {!editable && (
            <p className="sub">
              Смету меняет владелец или менеджер бренда. Твоя роль видит цифры, но не правит их.
            </p>
          )}
        </div>

        {/* ── отчёт ── */}
        <div className="stack">
          <Card>
            <div className="h2" style={{ marginBottom: 12 }}>
              Отдача
            </div>
            <KeyRow label="Выручка по акции" mono>
              {r.ready ? money(r.revenue) : '—'}
            </KeyRow>
            <KeyRow label="Маржинальная прибыль" mono>
              {r.ready ? money(r.gross_profit) : '—'}
            </KeyRow>
            <KeyRow label="Прибыль после расходов" mono>
              {r.ready ? money(r.net_profit) : '—'}
            </KeyRow>
            <KeyRow label="ROI" mono>
              {r.ready ? `${percentBp(r.roi_bp)}%` : '—'}
            </KeyRow>
            <KeyRow label="ROMI (без призового фонда)" mono>
              {r.ready && r.romi_bp !== 0 ? `${percentBp(r.romi_bp)}%` : '—'}
            </KeyRow>
            <KeyRow label="Остаток бюджета" mono>
              {r.input.budget > 0 ? money(r.budget_left) : '—'}
            </KeyRow>
          </Card>

          <Card>
            <div className="h2" style={{ marginBottom: 12 }}>
              Привлечение
            </div>
            <KeyRow label="CAC · новый клиент" mono>
              {r.cac > 0 ? money(r.cac) : '—'}
            </KeyRow>
            <KeyRow label="CPA · одно участие" mono>
              {r.cpa > 0 ? money(r.cpa) : '—'}
            </KeyRow>
            <KeyRow label="Стоимость участника" mono>
              {r.cost_per_participant > 0 ? money(r.cost_per_participant) : '—'}
            </KeyRow>
            <KeyRow label="LTV за горизонт" mono>
              {r.ready ? money(r.ltv) : '—'}
            </KeyRow>
            <KeyRow label="LTV к CAC" mono>
              {r.ltv_to_cac_bp > 0 ? timesBp(r.ltv_to_cac_bp) : '—'}
            </KeyRow>
            <KeyRow label="Покупок до окупаемости" mono>
              {r.payback_purchases > 0 ? num(r.payback_purchases) : '—'}
            </KeyRow>
          </Card>

          <Card>
            <div className="h2" style={{ marginBottom: 12 }}>
              Факты акции
            </div>
            <KeyRow label="Участников" mono>
              {num(r.facts.participants)}
            </KeyRow>
            <KeyRow label="Участий" mono>
              {num(r.facts.entries)}
            </KeyRow>
            <KeyRow label="Новых клиентов" mono>
              {num(r.facts.new_customers)}
            </KeyRow>
            <KeyRow label="Вернувшихся" mono>
              {num(r.facts.returning)}
            </KeyRow>
            <KeyRow label="Призов выдано" mono>
              {num(r.facts.prizes_issued)}
            </KeyRow>
            <KeyRow label="Себестоимость выданного" mono>
              {money(r.facts.prize_cost_actual)}
            </KeyRow>
            <KeyRow label="Тираж кодов" mono>
              {num(r.facts.codes_used)} из {num(r.facts.codes_issued)} ·{' '}
              {percentBp(r.redemption_bp)}%
            </KeyRow>
          </Card>

          {data.audience && (
            <Card>
              <div className="h2" style={{ marginBottom: 6 }}>
                Данные аудитории
              </div>
              <p className="sub" style={{ marginBottom: 12 }}>
                Обезличенно: регион, новый или вернувшийся, частота участий. Телефонов, имён и
                номеров билетов здесь нет и не будет — бренду обещана статистика, а не база
                покупателей.
              </p>

              <div className="lab" style={{ marginBottom: 8 }}>
                Новые и вернувшиеся
              </div>
              <Bars
                rows={[
                  { label: 'впервые у бренда', count: data.audience.new },
                  { label: 'участвовали раньше', count: data.audience.returning },
                ]}
              />

              <div className="lab" style={{ margin: '16px 0 8px' }}>
                Регионы
              </div>
              <Bars rows={data.audience.by_region.map((a) => ({ label: a.key, count: a.count }))} />

              <div className="lab" style={{ margin: '16px 0 8px' }}>
                Частота участий
              </div>
              <Bars
                rows={data.audience.frequency.map((a) => ({
                  label: a.label ?? a.key,
                  count: a.count,
                }))}
              />
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <div className="stat">
      <div className="v" style={tone ? { color: `var(--${tone})` } : undefined}>
        {value}
      </div>
      <div className="l">{label}</div>
    </div>
  );
}

/** Поле суммы: вводится в сомони, хранится в дирамах. */
function MoneyField({
  label,
  hint,
  value,
  error,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  error?: string;
  disabled?: boolean;
  onChange: (minor: number) => void;
}) {
  // Поле держит свою строку, пока его правят: иначе «4 500,» на полпути к
  // «4 500,50» превратится в «4 500» и съест ввод следующей цифры.
  const [text, setText] = useState(() => moneyInput(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(moneyInput(value));
  }, [value, focused]);

  return (
    <Field label={label} hint={hint} error={error}>
      <input
        className="input mono"
        inputMode="decimal"
        disabled={disabled}
        value={text}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setText(moneyInput(value));
        }}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseMoney(e.target.value));
        }}
      />
    </Field>
  );
}

/** Сумма для поля ввода: без разделителей разрядов, чтобы её можно было править. */
function moneyInput(minor: number): string {
  if (!minor) return '';
  const abs = Math.abs(minor);
  const major = Math.floor(abs / 100);
  const cents = abs % 100;
  const sign = minor < 0 ? '-' : '';
  return cents > 0 ? `${sign}${major},${String(cents).padStart(2, '0')}` : `${sign}${major}`;
}

/** Горизонтальные полосы разреза — та же разметка, что у воронки акции. */
function Bars({ rows }: { rows: { label: string; count: number }[] }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  if (max === 0) return <p className="sub">Пока нет участников.</p>;
  return (
    <div className="funnel">
      {rows.map((row) => (
        <div key={row.label} className="fr">
          <span className="sub">{row.label}</span>
          <span className="bar">
            <i style={{ width: `${Math.round((100 * row.count) / max)}%` }} />
          </span>
          <span className="n mono">{num(row.count)}</span>
        </div>
      ))}
    </div>
  );
}
